import React from "react"
import { type ColumnDef } from "@tanstack/react-table"
import { ScanEye, ArrowUpDown, ShieldCheck, Loader, FileText, CheckCircle, Truck, Package, Edit, DollarSign, Check } from 'lucide-react';
import { Button } from "../../components/button"
import { Badge } from "../../components/badge"
import TagsCell from "./TagsCell"
import DueDateChip from "../DueDateChip"
import { type AdminTag } from "./AddLabels"
import { format } from "date-fns";

export type OrderList = {
  id: string;
  customer_id: number;
  order_id: string;
  job_title: string;
  product_type: string;
  product_name: string;
  quantity: number;
  size: string;
  metal_type: string;
  special_instructions: string;
  media_attachment: string[];
  resin_sample_model: number;
  is_draft: number; // use `boolean` if converted from tinyint(1)
  status: string;
  labels: string[];
  order_type: string;
  is_new_update: number; // or boolean
  created_at: string;
  updated_at: string;
  column: string
  c_name?: string
  c_business_name?: string
  due_date?: string | null
}

export const baseColumns: ColumnDef<OrderList>[] = [
  {
    accessorKey: "order_id",
    header: "Order ID",
    cell: ({ row }) => <span>#{row.original.order_id}</span>
  },
  {
    accessorKey: "job_title",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="!p-0"
        >
          Job Title
          <ArrowUpDown />
        </Button>
      )
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: "product_type",
    enableColumnFilter: true,
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="!p-0"
        >
          Type of Jewelry
          <ArrowUpDown />
        </Button>
      )
    },
  },
  {
    accessorKey: "updated_at",
    header: "Date Modified",
    cell: ({ row }) => <span>{format(row.original.updated_at, 'dd MMMM yyyy')}</span>
  },
  {
    accessorKey: "created_at",
    header: "Date of Creation",
    cell: ({ row }) => <span>{format(row.original.created_at, 'dd MMMM yyyy')}</span>
  },
  {
    accessorKey: "resin_sample_model",
    header: "Resin Sample Model",
    cell: ({ row }) => {
      const resinSample = row.original.resin_sample_model;
      return <span>{resinSample === 1 ? 'Provide sample' : 'None'}</span>;
    }
  },
  {
    accessorKey: "metal_type",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="!p-0"
        >
          Metal Type
          <ArrowUpDown />
        </Button>
      )
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: "size",
    header: "Size",
  },
  {
    accessorKey: "quantity",
    header: "Quantity",
    cell: ({ row }) => (
      <span>{row.original.quantity} pcs</span>
    )
  },
  {
    accessorKey: "status",
    header: "Status",
    enableColumnFilter: true,
    cell: ({ row }) => (
      <Badge variant="outline" className="text-muted-foreground px-1.5">
        {row.original.status === "under_review" ? (
          <ShieldCheck className="fill-green-500 dark:fill-green-400" />
        ) : (row.original.status === "draft" ? (
          <ShieldCheck className="fill-yellow-500 dark:fill-yellow-400" />) :
          (
            <Loader />)
        )}
        {row.original.status}
      </Badge>
    ),
  },
  {
    accessorKey: "order_type",
    header: "Order Type",
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <ScanEye size={16} strokeWidth={1} className="h-4 w-4" />
        </Button>
      )
    },
  },
]

// Customer column (only for admin)
const customerColumn: ColumnDef<OrderList> = {
  accessorKey: "c_business_name",
  header: "Customer Name",
  cell: ({ row }) => <span>{row.original.c_business_name}</span>,
}

// Labels column (only for non-admins)
const labelsColumn = (onUpdateOrder?: any, adminTags?: AdminTag[], fetchTags?: any): ColumnDef<OrderList> => ({
  accessorKey: "labels",
  header: "Labels",
  enableColumnFilter: true,
  cell: ({ row }) => (
    <TagsCell
      labels={row.original.labels}
      orderId={row.original.order_id}
      orderRef={row.original.id}
      customerId={row.original.customer_id.toString()}
      newUpdate={Boolean(row.original.is_new_update)}
      adminTags={adminTags}
      fetchTags={fetchTags}
      onUpdateOrder={onUpdateOrder}
    />
  ),
})


// Due date column (admin only -- the date is internal scheduling, and the API
// strips it from customer payloads entirely).
const dueDateColumn: ColumnDef<OrderList> = {
  accessorKey: "due_date",
  header: ({ column }) => (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      className="!p-0"
    >
      Due Date
      <ArrowUpDown />
    </Button>
  ),
  // Stored as 'YYYY-MM-DD', which sorts correctly as a plain string, so the
  // default alphanumeric sort is already chronological.
  cell: ({ row }) =>
    row.original.due_date
      ? <DueDateChip value={row.original.due_date} />
      : <span className="text-muted-foreground">—</span>,
}

export const getColumns = (isAdmin: boolean, onUpdateOrder?: any, adminTags?: AdminTag[], fetchTags?: any): ColumnDef<OrderList>[] => {
  const cols = [...baseColumns]

  // Insert labels or customer column at the beginning (or desired position)
  if (isAdmin) {
    // Before the trailing actions column, so the row-open button stays last.
    const actionsAt = cols.findIndex(c => c.id === "actions")
    cols.splice(actionsAt === -1 ? cols.length : actionsAt, 0, dueDateColumn)
    cols.unshift(customerColumn)
    cols.unshift(labelsColumn(onUpdateOrder, adminTags, fetchTags))
  } else {
    cols.unshift(labelsColumn(onUpdateOrder, [], fetchTags))
  }
  return cols
}